import { FormulaEquation } from '../../types';

export const DYNAMICS_EQUATIONS: FormulaEquation[] = [
  {
    id: 'segunda_lei_newton',
    name: '2ª Lei de Newton (Princípio Fundamental)',
    subject: 'FISICA',
    latex: 'F = m \\cdot a',
    variables: ['F', 'm', 'a'],
    description: 'A força resultante é diretamente proporcional à massa do corpo e à sua aceleração.',
    rearrangedLatex: {
      F: 'F = m \\cdot a',
      m: 'm = \\frac{F}{a}',
      a: 'a = \\frac{F}{m}',
    },
    descriptorCode: 'FIS_D6',
  },
  {
    id: 'forca_peso',
    name: 'Força Peso',
    subject: 'FISICA',
    latex: 'P = m \\cdot g',
    variables: ['P_peso', 'm', 'g'],
    description: 'Força de atração gravitacional atuante sobre um corpo de massa m.',
    rearrangedLatex: {
      P_peso: 'P = m \\cdot g',
      m: 'm = \\frac{P}{g}',
      g: 'g = \\frac{P}{m}',
    },
    descriptorCode: 'FIS_D6',
  },
  {
    id: 'forca_atrito',
    name: 'Força de Atrito Cinético',
    subject: 'FISICA',
    latex: 'F_{at} = \\mu \\cdot N',
    variables: ['Fat', 'mu', 'N'],
    description: 'Força dissipativa proporcional ao coeficiente de atrito e à compressão normal.',
    rearrangedLatex: {
      Fat: 'F_{at} = \\mu \\cdot N',
      mu: '\\mu = \\frac{F_{at}}{N}',
      N: 'N = \\frac{F_{at}}{\\mu}',
    },
    descriptorCode: 'FIS_D6',
    alternateDescriptorCodes: ['FIS_D8'],
  },
  {
    id: 'quantidade_movimento',
    name: 'Quantidade de Movimento (Momento Linear)',
    subject: 'FISICA',
    latex: 'Q = m \\cdot v',
    variables: ['Q_mov', 'm', 'v'],
    description: 'Produto da massa pelo vetor velocidade instantânea.',
    rearrangedLatex: {
      Q_mov: 'Q = m \\cdot v',
      m: 'm = \\frac{Q}{v}',
      v: 'v = \\frac{Q}{m}',
    },
    descriptorCode: 'FIS_D6',
  },
  {
    id: 'delta_q',
    name: 'Variação da Quantidade de Movimento',
    subject: 'FISICA',
    latex: '\\Delta Q = m \\cdot \\Delta v',
    variables: ['deltaQ', 'm', 'deltav'],
    description: 'Variação do momento linear proporcional à variação de velocidade.',
    rearrangedLatex: {
      deltaQ: '\\Delta Q = m \\cdot \\Delta v',
      m: 'm = \\frac{\\Delta Q}{\\Delta v}',
      deltav: '\\Delta v = \\frac{\\Delta Q}{m}',
    },
    descriptorCode: 'FIS_D6',
  },
  {
    id: 'teorema_impulso',
    name: 'Impulso de uma Força Constante',
    subject: 'FISICA',
    latex: 'I = F \\cdot \\Delta t',
    variables: ['Imp', 'F', 'deltat'],
    description: 'Impulso produzido por uma força atuando durante um intervalo de tempo.',
    rearrangedLatex: {
      Imp: 'I = F \\cdot \\Delta t',
      F: 'F = \\frac{I}{\\Delta t}',
      deltat: '\\Delta t = \\frac{I}{F}',
    },
    descriptorCode: 'FIS_D6',
  },
  {
    id: 'impulso_delta_q',
    name: 'Teorema do Impulso e Momento',
    subject: 'FISICA',
    latex: 'I = \\Delta Q = m \\cdot \\Delta v',
    variables: ['Imp', 'deltaQ'],
    description: 'O impulso da força resultante equivale à variação da quantidade de movimento.',
    rearrangedLatex: {
      Imp: 'I = \\Delta Q',
      deltaQ: '\\Delta Q = I',
    },
    descriptorCode: 'FIS_D6',
  },
  {
    id: 'energia_cinetica',
    name: 'Energia Cinética',
    subject: 'FISICA',
    latex: 'E_c = \\frac{1}{2} m v^2',
    variables: ['Ec', 'm', 'v'],
    description: 'Energia escalar associada à velocidade e movimento da massa.',
    rearrangedLatex: {
      Ec: 'E_c = \\frac{1}{2} m v^2',
      m: 'm = \\frac{2E_c}{v^2}',
      v: 'v = \\sqrt{\\frac{2E_c}{m}}',
    },
    descriptorCode: 'FIS_D7',
  },
  {
    id: 'energia_potencial',
    name: 'Energia Potencial Gravitacional',
    subject: 'FISICA',
    latex: 'E_p = m \\cdot g \\cdot \\Delta S',
    variables: ['Ep', 'm', 'g', 'deltaS'],
    description: 'Energia potencial acumulada em função da altura no campo gravitacional.',
    rearrangedLatex: {
      Ep: 'E_p = m \\cdot g \\cdot \\Delta S',
      m: 'm = \\frac{E_p}{g \\cdot \\Delta S}',
      g: 'g = \\frac{E_p}{m \\cdot \\Delta S}',
      deltaS: '\\Delta S = \\frac{E_p}{m \\cdot g}',
    },
    descriptorCode: 'FIS_D7',
  },
  {
    id: 'energia_mecanica',
    name: 'Conservação da Energia Mecânica',
    subject: 'FISICA',
    latex: 'E_m = E_c + E_p',
    variables: ['Em', 'Ec', 'Ep'],
    description: 'Soma das energias cinética e potencial em um sistema físico.',
    rearrangedLatex: {
      Em: 'E_m = E_c + E_p',
      Ec: 'E_c = E_m - E_p',
      Ep: 'E_p = E_m - E_c',
    },
    descriptorCode: 'FIS_D7',
  },
  {
    id: 'trabalho_forca',
    name: 'Trabalho de Força Constante',
    subject: 'FISICA',
    latex: '\\tau = F \\cdot \\Delta S',
    variables: ['tau', 'F', 'deltaS'],
    description: 'Transferência de energia decorrente da ação de uma força ao longo do deslocamento.',
    rearrangedLatex: {
      tau: '\\tau = F \\cdot \\Delta S',
      F: 'F = \\frac{\\tau}{\\Delta S}',
      deltaS: '\\Delta S = \\frac{\\tau}{F}',
    },
    descriptorCode: 'FIS_D8',
  },
  {
    id: 'potencia_mecanica',
    name: 'Potência Mecânica Média',
    subject: 'FISICA',
    latex: 'P_{ot} = \\frac{\\tau}{\\Delta t}',
    variables: ['Pot', 'tau', 'deltat'],
    description: 'Taxa de execução de trabalho mecânico por unidade de tempo.',
    rearrangedLatex: {
      Pot: 'P_{ot} = \\frac{\\tau}{\\Delta t}',
      tau: '\\tau = P_{ot} \\cdot \\Delta t',
      deltat: '\\Delta t = \\frac{\\tau}{P_{ot}}',
    },
    descriptorCode: 'FIS_D8',
    alternateDescriptorCodes: ['FIS_D28'],
  },
  {
    id: 'potencia_fv',
    name: 'Potência Mecânica Instantânea',
    subject: 'FISICA',
    latex: 'P_{ot} = F \\cdot v',
    variables: ['Pot', 'F', 'v'],
    description: 'Potência instantânea desenvolvida por força paralela à velocidade do corpo.',
    rearrangedLatex: {
      Pot: 'P_{ot} = F \\cdot v',
      F: 'F = \\frac{P_{ot}}{v}',
      v: 'v = \\frac{P_{ot}}{F}',
    },
    descriptorCode: 'FIS_D8',
  },
];
